import React, { useState, useEffect, useCallback } from 'react';
import { medicamentoService } from '../services';
import Modal from './Modal';
import { 
  Search, 
  Package,
  AlertTriangle,
  CheckCircle,
  Filter,
  X
} from 'lucide-react';

const SeletorMedicamentos = ({ isOpen, onClose, onSelect, medicamentosSelecionados = [] }) => {
  const [medicamentos, setMedicamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Estados dos filtros
  const [filtros, setFiltros] = useState({
    categoria: '',
    tipoControle: '',
    statusEstoque: '',
    somenteComEstoque: false
  });
  const [showFiltros, setShowFiltros] = useState(false);

  const carregarMedicamentos = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 12,
        search,
        categoria: filtros.categoria,
        tipoControle: filtros.tipoControle,
        somenteComEstoque: filtros.somenteComEstoque
      };
      
      const response = await medicamentoService.getGrupos(params);
      let medicamentosFiltrados = response.grupos || [];
      
      setMedicamentos(medicamentosFiltrados);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Erro ao carregar medicamentos:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, filtros]);

  useEffect(() => {
    if (isOpen) {
      carregarMedicamentos();
    }
  }, [isOpen, carregarMedicamentos]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleFiltroChange = (key, value) => {
    setFiltros(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const limparFiltros = () => {
    setFiltros({
      categoria: '',
      tipoControle: '',
      statusEstoque: '',
      somenteComEstoque: false
    });
    setCurrentPage(1);
  };

  const temFiltrosAtivos = () => {
    return filtros.categoria || filtros.tipoControle || filtros.statusEstoque || filtros.somenteComEstoque;
  };

  const handleSelectMedicamento = (medicamento) => {
    // Verificar se já está selecionado
    const jaSelecionado = medicamentosSelecionados.some(item => item.id === medicamento.id);
    
    if (jaSelecionado) {
      return; // Não fazer nada se já estiver selecionado
    }

    // Verificar se tem estoque total disponível
    if (medicamento.estoqueTotal === 0) {
      return; // Não permitir seleção de medicamentos sem estoque
    }
    
    onSelect(medicamento);
  };

  const getStatusEstoque = (medicamento) => {
    const estoqueTotal = medicamento.estoqueTotal || 0;
    const estoqueMinimo = medicamento.estoqueMinimo || 0;
    
    if (estoqueTotal === 0) {
      return { color: '#dc3545', text: 'Sem Estoque', icon: AlertTriangle };
    } else if (estoqueTotal <= estoqueMinimo) {
      return { color: '#ffc107', text: 'Estoque Baixo', icon: AlertTriangle };
    } else {
      return { color: '#28a745', text: 'Disponível', icon: CheckCircle };
    }
  };

  const getTipoControleColor = (tipo) => {
    const cores = {
      'sem_controle': '#6c757d',
      'normal': '#007bff',
      'especial': '#dc3545'
    };
    return cores[tipo] || '#6c757d';
  };

  const getTipoControleText = (tipo) => {
    const textos = {
      'sem_controle': 'Sem Controle',
      'normal': 'Controle Normal',
      'especial': 'Controle Especial'
    };
    return textos[tipo] || tipo;
  };

  const isMedicamentoSelecionado = (medicamento) => {
    return medicamentosSelecionados.some(item => item.id === medicamento.id);
  };


  const podeSelecionar = (medicamento) => {
    return medicamento.estoqueTotal > 0 && !isMedicamentoSelecionado(medicamento);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Selecionar Medicamentos" 
      size="xlarge"
    >
      {/* Busca */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ position: 'relative', marginBottom: '15px' }}>
          <Search size={20} style={{ 
            position: 'absolute', 
            left: '10px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: '#667'
          }} />
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por nome, princípio ativo ou fabricante..."
            value={search}
            onChange={handleSearch}
            style={{ paddingLeft: '40px' }}
            autoFocus
          />
        </div>

        {/* Botão de Filtros */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setShowFiltros(!showFiltros)}
            className={`btn ${showFiltros ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
            style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            <Filter size={16} />
            Filtros
            {temFiltrosAtivos() && (
              <span style={{
                backgroundColor: '#dc3545',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '5px'
              }}>
                !
              </span>
            )}
          </button>

          {temFiltrosAtivos() && (
            <button
              type="button"
              onClick={limparFiltros}
              className="btn btn-outline-secondary btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <X size={14} />
              Limpar Filtros
            </button>
          )}
        </div>

        {/* Painel de Filtros */}
        {showFiltros && (
          <div style={{
            marginTop: '15px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '15px'
            }}>
              {/* Filtro por Categoria */}
              <div>
                <label className="form-label" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  Categoria
                </label>
                <select
                  className="form-control form-control-sm"
                  value={filtros.categoria}
                  onChange={(e) => handleFiltroChange('categoria', e.target.value)}
                >
                  <option value="">Todas as categorias</option>
                  <option value="Analgésico">Analgésico</option>
                  <option value="Antibiótico">Antibiótico</option>
                  <option value="Anti-inflamatório">Anti-inflamatório</option>
                  <option value="Cardiovascular">Cardiovascular</option>
                  <option value="Dermatológico">Dermatológico</option>
                  <option value="Digestivo">Digestivo</option>
                  <option value="Endócrino">Endócrino</option>
                  <option value="Neurológico">Neurológico</option>
                  <option value="Respiratório">Respiratório</option>
                  <option value="Outros">Outros</option>
                </select>
              </div>

              {/* Filtro por Tipo de Controle */}
              <div>
                <label className="form-label" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  Tipo de Controle
                </label>
                <select
                  className="form-control form-control-sm"
                  value={filtros.tipoControle}
                  onChange={(e) => handleFiltroChange('tipoControle', e.target.value)}
                >
                  <option value="">Todos os tipos</option>
                  <option value="sem_controle">Sem Controle</option>
                  <option value="normal">Controle Normal</option>
                  <option value="especial">Controle Especial</option>
                </select>
              </div>

              {/* Filtro por Status de Estoque */}
              <div>
                <label className="form-label" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  Status do Estoque
                </label>
                <select
                  className="form-control form-control-sm"
                  value={filtros.statusEstoque}
                  onChange={(e) => handleFiltroChange('statusEstoque', e.target.value)}
                >
                  <option value="">Todos os status</option>
                  <option value="disponivel">Disponível</option>
                  <option value="baixo">Estoque Baixo</option>
                  <option value="zerado">Sem Estoque</option>
                </select>
              </div>

              {/* Checkbox Somente com Estoque */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="somenteComEstoque"
                  checked={filtros.somenteComEstoque}
                  onChange={(e) => handleFiltroChange('somenteComEstoque', e.target.checked)}
                  style={{ marginTop: '20px' }}
                />
                <label htmlFor="somenteComEstoque" style={{ 
                  fontSize: '14px', 
                  fontWeight: 'bold',
                  marginTop: '20px',
                  cursor: 'pointer'
                }}>
                  Somente com estoque
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Grid de Medicamentos */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner"></div>
        </div>
      ) : medicamentos.length > 0 ? (
        <>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '15px',
            maxHeight: '500px', 
            overflowY: 'auto',
            padding: '5px'
          }}>
            {medicamentos.map((medicamento) => {
              const statusEstoque = getStatusEstoque(medicamento);
              const StatusIcon = statusEstoque.icon;
              const jaSelecionado = isMedicamentoSelecionado(medicamento);
              const podeSerSelecionado = podeSelecionar(medicamento);
              
              return (
                <div
                  key={medicamento.id}
                  onClick={() => podeSerSelecionado ? handleSelectMedicamento(medicamento) : null}
                  style={{
                    border: `2px solid ${jaSelecionado ? '#28a745' : '#ddd'}`,
                    borderRadius: '8px',
                    padding: '15px',
                    cursor: podeSerSelecionado ? 'pointer' : 'not-allowed',
                    backgroundColor: jaSelecionado ? '#d4edda' : 'white',
                    transition: 'all 0.2s',
                    opacity: !podeSerSelecionado ? 0.6 : 1,
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (podeSerSelecionado) {
                      e.currentTarget.style.borderColor = '#007bff';
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!jaSelecionado) {
                      e.currentTarget.style.borderColor = '#ddd';
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  {/* Header do Card */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#333',
                        lineHeight: '1.3'
                      }}>
                        {medicamento.nome}
                      </h4>
                      
                      {medicamento.principioAtivo && (
                        <p style={{ 
                          margin: '0 0 5px 0', 
                          fontSize: '14px', 
                          color: '#667',
                          fontStyle: 'italic'
                        }}>
                          {medicamento.principioAtivo}
                        </p>
                      )}
                    </div>

                    {/* Status de Seleção */}
                    {jaSelecionado && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        color: '#28a745',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        <CheckCircle size={16} />
                        Adicionado
                      </div>
                    )}
                  </div>

                  {/* Informações do Medicamento */}
                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px',
                    marginBottom: '12px',
                    fontSize: '13px'
                  }}>
                    <div>
                      <strong>Código de Barras:</strong><br />
                      <span style={{ color: '#667' }}>
                        {medicamento.codigoBarras || 'Não informado'}
                      </span>
                    </div>
                    
                    <div>
                      <strong>Categoria:</strong><br />
                      <span style={{ color: '#667' }}>
                        {medicamento.categoria || 'Não informada'}
                      </span>
                    </div>
                    
                    <div>
                      <strong>Estoque Total:</strong><br />
                      <span style={{ 
                        color: statusEstoque.color,
                        fontWeight: 'bold'
                      }}>
                        {medicamento.estoqueTotal} unidades
                      </span>
                    </div>
                    
                    <div>
                      <strong>Nº de Lotes:</strong><br />
                      <span style={{ color: '#667' }}>
                        {medicamento.totalLotes} lote{medicamento.totalLotes !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Footer do Card */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    paddingTop: '12px',
                    borderTop: '1px solid #eee'
                  }}>
                    {/* Tipo de Controle */}
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      backgroundColor: getTipoControleColor(medicamento.tipoControle),
                      color: 'white'
                    }}>
                      {getTipoControleText(medicamento.tipoControle)}
                    </span>

                    {/* Status do Estoque */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: statusEstoque.color
                    }}>
                      <StatusIcon size={14} />
                      {statusEstoque.text}
                    </div>
                  </div>

                  {/* Overlay para medicamentos sem estoque */}
                  {medicamento.estoqueTotal === 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      SEM ESTOQUE
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: '15px',
              marginTop: '20px',
              padding: '15px',
              borderTop: '1px solid #eee'
            }}>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn btn-secondary"
                style={{ padding: '8px 16px' }}
              >
                Anterior
              </button>
              
              <span style={{ color: '#667' }}>
                Página {currentPage} de {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="btn btn-secondary"
                style={{ padding: '8px 16px' }}
              >
                Próxima
              </button>
            </div>
          )}
        </>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          color: '#667'
        }}>
          <Package size={48} style={{ opacity: 0.5, marginBottom: '15px' }} />
          <h3>Nenhum medicamento encontrado</h3>
          <p>
            {search 
              ? 'Tente ajustar o termo de busca' 
              : 'Não há medicamentos cadastrados'
            }
          </p>
        </div>
      )}

      {/* Rodapé */}
      <div style={{ 
        borderTop: '1px solid #eee',
        padding: '15px 0 0 0',
        marginTop: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ fontSize: '14px', color: '#667' }}>
          {medicamentos.length > 0 && (
            <>
              Mostrando {medicamentos.length} medicamento{medicamentos.length !== 1 ? 's' : ''}
              {medicamentosSelecionados.length > 0 && (
                <span style={{ marginLeft: '10px', color: '#28a745', fontWeight: 'bold' }}>
                  • {medicamentosSelecionados.length} selecionado{medicamentosSelecionados.length !== 1 ? 's' : ''}
                </span>
              )}
            </>
          )}
        </div>
        
        <button 
          onClick={onClose}
          className="btn btn-primary"
        >
          Fechar
        </button>
      </div>
    </Modal>
  );
};

export default SeletorMedicamentos;